import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getErrorMessage } from '../../shared/utils/errorUtils';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api/v1';

interface SystemStats {
  users: { total: number; active: number; by_type: { account_type: string; count: number }[] };
  groups: { total: number; pending: number; approved: number; by_type: { group_type: string; count: number }[] };
  memberships: { total: number; active: number };
  parent_child_connections: { total: number; approved: number };
  tests: { users_with_tests: string; total_tests: string; avg_score: number | null };
}

interface PendingGroup {
  id: string;
  group_name: string;
  group_type: string;
  created_at: string;
  owner_name: string;
  owner_email: string;
}

interface ManagerStats {
  total_groups: number;
  total_members: number;
  active_members: number;
  total_tests: number;
  avg_score: number;
}

interface AdminDashboardState {
  systemStats: SystemStats | null;
  pendingGroups: PendingGroup[];
  managerStats: ManagerStats | null;
  loading: boolean;
  error: string | null;
}

const initialState: AdminDashboardState = {
  systemStats: null,
  pendingGroups: [],
  managerStats: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchSystemStats = createAsyncThunk(
  'adminDashboard/fetchSystemStats',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/admin/dashboard/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch statistics'));
    }
  }
);

export const fetchPendingGroups = createAsyncThunk(
  'adminDashboard/fetchPendingGroups',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/admin/dashboard/pending-groups`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch pending groups'));
    }
  }
);

export const approveGroup = createAsyncThunk(
  'adminDashboard/approveGroup',
  async (groupId: string, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/admin/dashboard/groups/${groupId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to approve group'));
    }
  }
);

export const rejectGroup = createAsyncThunk(
  'adminDashboard/rejectGroup',
  async ({ groupId, reason }: { groupId: string; reason?: string }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/admin/dashboard/groups/${groupId}/reject`,
        { rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to reject group'));
    }
  }
);

export const fetchManagerStats = createAsyncThunk(
  'adminDashboard/fetchManagerStats',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/admin/dashboard/manager/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch manager stats'));
    }
  }
);

export const createBackup = createAsyncThunk(
  'adminDashboard/createBackup',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/admin/dashboard/backup/create`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to create backup'));
    }
  }
);

const adminDashboardSlice = createSlice({
  name: 'adminDashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemStats.fulfilled, (state, action: PayloadAction<SystemStats>) => {
        state.loading = false;
        state.systemStats = action.payload;
      })
      .addCase(fetchSystemStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchPendingGroups.fulfilled, (state, action: PayloadAction<PendingGroup[]>) => {
        state.pendingGroups = action.payload;
      })
      .addCase(approveGroup.fulfilled, (state, action) => {
        state.pendingGroups = state.pendingGroups.filter((g) => g.id !== action.payload.id);
      })
      .addCase(rejectGroup.fulfilled, (state, action) => {
        state.pendingGroups = state.pendingGroups.filter((g) => g.id !== action.payload.id);
      })
      .addCase(fetchManagerStats.fulfilled, (state, action: PayloadAction<ManagerStats>) => {
        state.managerStats = action.payload;
      });
  },
});

export const { clearError } = adminDashboardSlice.actions;
export default adminDashboardSlice.reducer;
